import { Box, BoxProps, ButtonBase, Tooltip, Divider } from '@mui/material';
import Typography from '@mui/material/Typography';
import DoctorText from 'pages/Dashboard/components/DoctorText';
import { TabLabels } from 'pages/Dashboard/components/MedicationCard';
import NoData from 'pages/Dashboard/components/NoData';
import {
  ShowMoreDrawerChildProps,
  sliceForShowMore,
} from 'pages/Dashboard/ShowMoreDrawer';
import { Diagnosis, DiagnosisCategory } from 'pages/Dashboard/types/diagnosis.types';
import * as React from 'react';
import { googleDateToDateString } from 'utils/dateUtils';
import {
  findPreferredDiagnosisCode,
  getDiagnosisDescription,
  getDiagnosisDescriptionV3,
} from 'utils/patientUtils';
import theme from 'theme';
import Tabs from 'pages/Dashboard/components/Tabs';
import { NavigateBefore } from '@mui/icons-material';
import { SourceTypeMap } from 'pages/Dashboard/types';
import { ReactComponent as User } from 'assets/UserIcon.svg';
import DiagnosisDrawer from 'pages/Dashboard/components/DiagnosisDrawer';
import { useParams } from 'react-router-dom';
import { trackMixpanelEvent } from 'utils/utilMethods';
import { MixpanelEventName } from 'utils/constants';
import NoHealthRecordDataCard, { HealthRecordCategory } from 'components/NoHealthRecordDataCard';
import PatientDetailsCard from 'components/PatientDetailsCard';
import HLCard from 'components/HLCard';

export interface DiagnosisCardProps extends ShowMoreDrawerChildProps {
  gridSize?: number;
  diagnosis?: Diagnosis[];
  showTabs?: boolean;
  drawerTabIndex?: number;
  onShowMoreDiagnosis?: (d: Diagnosis[], idx?: number) => void;
}

interface DiagnosisDetailsProps extends BoxProps {
  diagnosis: Diagnosis[];
  children?: React.ReactNode;
  handleSelectedDiagnosis: (index: number) => void;
}

function DiagnosisDetails({
  diagnosis,
  children,
  handleSelectedDiagnosis,
  ...boxProps
}: DiagnosisDetailsProps) {
  const unknownDateDiagnosis: Diagnosis[] = [];
  const knownDateDiagnosis: Diagnosis[] = [];

  diagnosis.forEach((current) => {
    if (current.started) {
      knownDateDiagnosis.push(current);
    }
    else {
      unknownDateDiagnosis.push(current);
    }
  });

  return (
    <Box {...boxProps}>
      {knownDateDiagnosis.length ? (
        <>
          {knownDateDiagnosis.map((currentDiagnosis, index) => {
            const isSameDate = googleDateToDateString(currentDiagnosis.onsetDate)
              === googleDateToDateString(currentDiagnosis.latestDate);
            const dateString = isSameDate ? googleDateToDateString(currentDiagnosis.onsetDate) : `${googleDateToDateString(currentDiagnosis.onsetDate)}${
              currentDiagnosis.latestDate
                ? ` - ${googleDateToDateString(currentDiagnosis.latestDate)}`
                : ''
            }`;
            const diagnosisCode = findPreferredDiagnosisCode(currentDiagnosis.diagnosis.codes);

            return (
              <React.Fragment key={`current-regimen-${currentDiagnosis.id}`}>
                <ButtonBase
                  key={`current-regimen-${currentDiagnosis.id}`}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'row',
                  }}
                  onClick={() => handleSelectedDiagnosis(index)}
                >
                  <Box
                    style={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      alignItems: 'flex-start',
                      marginRight: 24,
                    }}
                  >
                    {!!diagnosisCode && (
                      <Typography variant='body2' color='text.primary' textAlign='left' width='100%'>
                        {getDiagnosisDescriptionV3(
                          diagnosisCode,
                          currentDiagnosis.diagnosis?.displayName,
                        )}
                      </Typography>
                    )}
                    <Typography variant='body2' color='text.secondary' className='fs-mask'>
                      {dateString}
                    </Typography>
                    <DoctorText doctor={currentDiagnosis.doctor} />
                  </Box>
                  <Box style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                    {currentDiagnosis.occurrenceCount > 1 ? (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: '20px',
                          borderRadius: '64px',
                          backgroundColor: theme.custom.colors.backgroundColorSecondary,
                          color: theme.custom.colors.lightTextSecondary,
                          padding: '0 6.5px',
                          mr: 1,
                          mt: 1,
                        }}
                      >
                        <Typography
                          color={theme.custom.colors.lightTextSecondary}
                          fontSize={12}
                        >
                          {currentDiagnosis.occurrenceCount}
                        </Typography>
                      </Box>
                    ) : null}
                    {currentDiagnosis.source === SourceTypeMap.SOURCE_TYPE_PATIENT_REPORTED ? (
                      <Tooltip title='This item was added by patient' placement='top'>
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: 20,
                            bgcolor: theme.custom.colors.backgroundColorSecondary,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            mt: 1,
                          }}
                        >
                          <User />
                        </Box>
                      </Tooltip>
                    ) : null}
                    <NavigateBefore
                      sx={{
                        color: theme.custom.colors.lightTextSecondary,
                        marginLeft: 'auto',
                        marginRight: 0,
                        transform: 'rotate(180deg)',
                      }}
                    />
                  </Box>
                </ButtonBase>
                {index < knownDateDiagnosis.length - 1 && <Divider sx={{ borderColor: theme.custom.colors.borderColor, width: '100%', my: 1 }} />}
              </React.Fragment>
            );
          })}
          {unknownDateDiagnosis.length ? (
            <>
              <Typography mb={2} mt={3} variant='body1' color={theme.custom.colors.lightTextSecondary}>
                Unknown dates
              </Typography>
              {unknownDateDiagnosis.map((unknownDiagnosis, idx) => {
                const unknownDiagnosisCode = findPreferredDiagnosisCode(
                  unknownDiagnosis.diagnosis.codes,
                );
                if (!unknownDiagnosisCode) {
                  return null;
                }
                return (
                  <React.Fragment key={`unknown-diagnosis-${unknownDiagnosis?.id}`}>
                    <Typography mb={2}>
                      {getDiagnosisDescription(
                        unknownDiagnosisCode,
                        unknownDiagnosis.diagnosis?.displayName,
                      )}
                    </Typography>
                    {idx < unknownDateDiagnosis.length - 1 && <Divider sx={{ borderColor: theme.custom.colors.borderColor, width: '100%', my: 1 }} />}
                  </React.Fragment>
                );
              })}
            </>
          ) : null}
          {children}
        </>
      ) : (
        <NoData />
      )}
    </Box>
  );
}

export default function DiagnosisCardNew({
  gridSize,
  diagnosis = [],
  showTabs,
  drawerTabIndex,
  drawerMode,
  onShowMoreDiagnosis,
}: DiagnosisCardProps): JSX.Element | null {
  const { id } = useParams();
  const [value, setValue] = React.useState(0);
  const [psychDiagnosis, setPsychDiagnosis] = React.useState<Diagnosis[]>([]);
  const [otherDiagnosis, setOtherDiagnosis] = React.useState<Diagnosis[]>([]);
  const [finalDiagnosisList, setFinalDiagnosisList] = React.useState<Diagnosis[]>([]);
  const [currentDiagnosisList, setCurrentDiagnosisList] = React.useState<Diagnosis[]>([]);
  const [selectedDiagnosis, setSelectedDiagnosis] = React.useState<Partial<Diagnosis>>();
  const [showDrawer, setShowDrawer] = React.useState(false);

  React.useEffect(() => {
    const nonDatedDiagnosis = diagnosis
      .filter((currentDiagnosis) => !currentDiagnosis.started)
      .sort((a, b) => getDiagnosisDescription(
        a.diagnosis.codes[0],
        a.diagnosis.displayName,
      ).localeCompare(
        getDiagnosisDescription(b.diagnosis.codes[0], b.diagnosis.displayName),
      ));
    const datedDiagnosis = diagnosis.filter((currentDiagnosis) => currentDiagnosis.started);
    const diagnosisList = [...datedDiagnosis, ...nonDatedDiagnosis];
    setFinalDiagnosisList(diagnosisList);
  }, [diagnosis]);

  const handleSelectedDiagnosis = (index: number) => {
    setSelectedDiagnosis(currentDiagnosisList?.[index]);
    trackMixpanelEvent(MixpanelEventName.PATIENT_DETAILS_DIAGNOSIS_NAME_CLICKED, {
      location: 'medical_history_tab_diagnosis_name',
    });
    toggleDrawer();
  };

  React.useEffect(() => {
    const psych = finalDiagnosisList?.filter(
      (currentDiagnosis) => currentDiagnosis
        .category
      === DiagnosisCategory.DIAGNOSIS_CATEGORY_PSYCH,
    );
    const others = finalDiagnosisList?.filter(
      (currentDiagnosis) => currentDiagnosis
        .category
      !== DiagnosisCategory.DIAGNOSIS_CATEGORY_PSYCH,
    );
    setPsychDiagnosis(psych);
    setOtherDiagnosis(others);
  }, [finalDiagnosisList]);

  React.useEffect(() => {
    setCurrentDiagnosisList(value === 0 ? psychDiagnosis : otherDiagnosis);
  }, [value, psychDiagnosis, otherDiagnosis]);
  const slicedDiagnosis = sliceForShowMore<Diagnosis>(finalDiagnosisList, !drawerMode, 6);

  const slicedCurrentDiagnosis = sliceForShowMore<Diagnosis>(currentDiagnosisList, !drawerMode, 6);
  const handleChangeTab = (tab: number) => {
    setValue(tab);
  };

  React.useEffect(() => {
    if (drawerMode) {
      setValue(drawerTabIndex || 0);
    }
  }, [drawerTabIndex, drawerMode]);

  const toggleDrawer = () => {
    setShowDrawer((prev) => !prev);
  };
  const handleShowMoreDiagnosis = (diagnosisList: Diagnosis[], val?: number) => {
    trackMixpanelEvent(MixpanelEventName.PATIENT_DETAILS_SHOW_MORE_DIAGNOSIS_CLICKED);
    onShowMoreDiagnosis?.(diagnosisList, val);
  };
  const codes = findPreferredDiagnosisCode(selectedDiagnosis?.diagnosis?.codes);
  const displayDiagnosisName = codes
    ? getDiagnosisDescriptionV3(codes, selectedDiagnosis?.diagnosis?.displayName)
    : selectedDiagnosis?.diagnosis?.displayName;

  const content = (() => {
    if (diagnosis?.length === 0) {
      return (
        <NoHealthRecordDataCard
          title='No diagnosis found'
          description='Data will be available here once there is a activity to record'
          healthRecordCategory={HealthRecordCategory.diagnoses}
        />
      );
    }
    if (showTabs) {
      return (
        <>
          <Box mb={2}>
            <Tabs
              tabItems={[
                { label: `${TabLabels.PSYCH} (${psychDiagnosis.length})`, value: 0 },
                { label: `${TabLabels.OTHER} (${otherDiagnosis.length})`, value: 1 },
              ]}
              selectedTab={value}
              setSelectedTab={(tab) => handleChangeTab(tab)}
            />
          </Box>
          <HLCard sx={{ p: drawerMode ? 1.5 : 0, border: 'none' }}>
            <DiagnosisDetails
              diagnosis={slicedCurrentDiagnosis}
              handleSelectedDiagnosis={handleSelectedDiagnosis}
            />
          </HLCard>
        </>
      );
    }
    return (
      <HLCard sx={{ p: drawerMode ? 1.5 : 0, border: 'none' }}>
        <DiagnosisDetails
          diagnosis={drawerMode ? diagnosis : slicedDiagnosis}
          handleSelectedDiagnosis={handleSelectedDiagnosis}
        />
      </HLCard>
    );
  })();

  if (drawerMode) {
    return (
      <>
        <DiagnosisDrawer
          diagnosisName={displayDiagnosisName ?? ''}
          diagnosisId={selectedDiagnosis?.id ?? ''}
          toggleDrawer={toggleDrawer}
          patientId={id ?? ''}
          isOpen={showDrawer}
        />
        {content}
      </>
    );
  }

  return (
    <PatientDetailsCard
      title='Diagnoses'
      showHeader={!drawerMode && diagnosis?.length > 0}
      gridProps={{ xs: 12, md: !drawerMode ? gridSize : undefined }}
      viewDetailsBtnLabel='View all'
      showViewDetails={!drawerMode && diagnosis?.length > 6 && !!handleShowMoreDiagnosis}
      onViewDetails={() => handleShowMoreDiagnosis?.(diagnosis, value)}
      key='current-regimen-diagnosis'
      showSubHeader={false}
    >
      <DiagnosisDrawer
        diagnosisName={displayDiagnosisName ?? ''}
        diagnosisId={selectedDiagnosis?.id ?? ''}
        toggleDrawer={toggleDrawer}
        patientId={id ?? ''}
        isOpen={showDrawer}
      />
      {content}
    </PatientDetailsCard>
  );
}
